import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { logger } from "../lib/logger";
import { useAuth } from "../contexts/AuthContext";
import { UserPlus, Check, X, Users, ChevronRight, Sparkles } from "lucide-react";
import { toast } from "sonner";

/**
 * ParentLinkPanel: shown on Dashboard for role=parent.
 * ChildLinkRequests: shown on Dashboard for any client/parent receiving a link request.
 */

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" }); }
  catch { return iso; }
}

export function ChildLinkRequests({ onChange }) {
  const [reqs, setReqs] = useState([]);

  const load = async () => {
    try { const { data } = await api.get("/child/link-requests"); setReqs(data); }
    catch (e) { logger.warn("child link-requests load failed:", e?.message); }
  };
  useEffect(() => { load(); }, []);

  const respond = async (id, action) => {
    try {
      await api.post(`/child/link-requests/${id}/${action}`);
      toast.success(action === "accept" ? "Linked — your caregiver can now see your progress" : "Request declined");
      load();
      onChange?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update"); }
  };

  if (reqs.length === 0) return null;

  return (
    <div className="brand-card p-6 mb-6 bg-gradient-to-br from-accent/25 to-secondary/10" data-testid="child-link-requests">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="text-primary" size={18} />
        <h3 className="font-extrabold text-[#3D405B]">Caregiver link request{reqs.length > 1 ? "s" : ""}</h3>
      </div>
      <div className="space-y-3">
        {reqs.map((r) => (
          <div key={r.request_id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-2xl bg-card/95" data-testid={`link-request-${r.request_id}`}>
            <div className="text-sm">
              <div className="font-bold text-[#3D405B]">{r.parent_name}</div>
              <div className="text-[#7A7D96]">{r.parent_email} · {fmtDate(r.created_at)}</div>
              <div className="text-xs text-[#7A7D96] mt-1">They'll see: session counts + summaries you receive. <strong>Not</strong>: messages, notes, or session details.</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => respond(r.request_id, "accept")} className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-secondary text-white text-sm font-bold hover:-translate-y-0.5 transition-all" data-testid={`accept-link-${r.request_id}`}><Check size={14} /> Accept</button>
              <button onClick={() => respond(r.request_id, "decline")} className="inline-flex items-center gap-1 px-4 py-2 rounded-full border border-[#3D405B]/15 text-[#3D405B] text-sm font-semibold hover:bg-[#3D405B]/5 transition-all" data-testid={`decline-link-${r.request_id}`}><X size={14} /> Decline</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ParentLinkPanel() {
  const { user } = useAuth();
  const [children, setChildren] = useState([]);
  const [pending, setPending] = useState([]);
  const [email, setEmail] = useState("");
  const [selectedChild, setSelectedChild] = useState(null);
  const [progress, setProgress] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const [c, r] = await Promise.all([
        api.get("/parent/children"),
        api.get("/parent/link-requests"),
      ]);
      setChildren(c.data);
      setPending(r.data.filter((x) => x.status === "pending"));
    } catch (e) { logger.warn("parent link panel load failed:", e?.message); }
  };
  useEffect(() => { if (user?.role === "parent") load(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  if (user?.role !== "parent") return null;

  const onAdd = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/parent/link-requests", { child_email: email });
      toast.success("Link request sent");
      setEmail("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send request");
    } finally { setSubmitting(false); }
  };

  const openProgress = async (child) => {
    setSelectedChild(child);
    setProgress(null);
    try {
      const { data } = await api.get(`/parent/children/${child.user_id}/progress`);
      setProgress(data);
    } catch (err) { toast.error("Could not load progress"); }
  };

  return (
    <section className="brand-card p-6 mb-8" data-testid="parent-link-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div className="flex items-center gap-2">
          <Users className="text-secondary" size={20} />
          <h2 className="text-xl font-extrabold text-[#3D405B]">Linked young people</h2>
        </div>
        <span className="text-xs text-[#7A7D96]">Limited view — privacy first.</span>
      </div>

      <form onSubmit={onAdd} className="flex flex-col sm:flex-row gap-2 mb-5" data-testid="parent-link-form">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Their account email"
          className="flex-1 px-4 py-3 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/40 focus:border-primary outline-none"
          data-testid="parent-link-email-input"
        />
        <button type="submit" disabled={submitting} className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-primary text-white font-bold shadow-md hover:-translate-y-0.5 transition-all disabled:opacity-60" data-testid="parent-link-submit-btn">
          <UserPlus size={15} /> {submitting ? "Sending…" : "Send link request"}
        </button>
      </form>

      {pending.length > 0 && (
        <div className="mb-5 p-4 rounded-2xl bg-muted/60">
          <div className="text-xs font-bold uppercase tracking-wider text-[#7A7D96] mb-2">Pending requests</div>
          <ul className="space-y-1 text-sm text-[#3D405B]">
            {pending.map((p) => (
              <li key={p.request_id} className="flex items-center justify-between"><span>{p.child_name} <span className="text-[#7A7D96]">({p.child_email})</span></span><span className="pill bg-accent/30 text-[#3D405B]">awaiting</span></li>
            ))}
          </ul>
        </div>
      )}

      {children.length === 0 ? (
        <div className="text-sm text-[#7A7D96]">No linked accounts yet. Send a link request above — they'll need to accept it from their dashboard.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {children.map((c) => (
            <button key={c.user_id} onClick={() => openProgress(c)} className="brand-card brand-card-hover p-4 flex items-center justify-between text-left" data-testid={`child-card-${c.user_id}`}>
              <div className="flex items-center gap-3">
                {c.picture ? <img src={c.picture} className="w-11 h-11 rounded-full object-cover" alt="" /> : <div className="w-11 h-11 rounded-full bg-accent flex items-center justify-center font-bold text-[#3D405B]">{c.name?.[0]}</div>}
                <div>
                  <div className="font-bold text-[#3D405B]">{c.name}</div>
                  <div className="text-xs text-[#7A7D96]">{c.email}</div>
                </div>
              </div>
              <ChevronRight size={16} className="text-[#7A7D96]" />
            </button>
          ))}
        </div>
      )}

      {selectedChild && (
        <div className="fixed inset-0 z-50 bg-[#3D405B]/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setSelectedChild(null)}>
          <div className="bg-card rounded-3xl w-full max-w-2xl p-7" onClick={(e) => e.stopPropagation()} data-testid="child-progress-modal">
            <div className="flex items-start justify-between mb-5">
              <div className="flex items-center gap-3">
                {selectedChild.picture ? <img src={selectedChild.picture} className="w-14 h-14 rounded-2xl object-cover" alt="" /> : <div className="w-14 h-14 rounded-2xl bg-accent flex items-center justify-center font-extrabold text-[#3D405B]">{selectedChild.name?.[0]}</div>}
                <div>
                  <div className="overline">Progress for</div>
                  <div className="text-xl font-extrabold text-[#3D405B]">{selectedChild.name}</div>
                </div>
              </div>
              <button onClick={() => setSelectedChild(null)} className="p-2 rounded-full hover:bg-[#3D405B]/5"><X size={18} /></button>
            </div>
            {!progress ? (
              <div className="text-[#7A7D96] text-center py-8">Loading…</div>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-3 mb-5">
                  <div className="brand-card p-4 text-center"><div className="text-2xl font-extrabold text-primary">{progress.counts.upcoming}</div><div className="text-xs text-[#7A7D96] uppercase tracking-wider font-bold mt-1">Upcoming</div></div>
                  <div className="brand-card p-4 text-center"><div className="text-2xl font-extrabold text-secondary">{progress.counts.completed}</div><div className="text-xs text-[#7A7D96] uppercase tracking-wider font-bold mt-1">Completed</div></div>
                  <div className="brand-card p-4 text-center"><div className="text-2xl font-extrabold text-[#5F627D]">{progress.counts.cancelled}</div><div className="text-xs text-[#7A7D96] uppercase tracking-wider font-bold mt-1">Cancelled</div></div>
                </div>
                <h4 className="font-bold text-[#3D405B] mb-2">Shared session summaries</h4>
                {progress.summaries.length === 0 ? (
                  <p className="text-sm text-[#7A7D96]">No summaries shared yet. Counsellors share summaries after sessions, and only those marked visible appear here.</p>
                ) : (
                  <ul className="space-y-3 max-h-72 overflow-auto pr-1">
                    {progress.summaries.map((s) => (
                      <li key={s.booking_id} className="p-3 rounded-2xl bg-secondary/10 border border-secondary/20">
                        <div className="text-xs text-[#7A7D96] mb-1">{fmtDate(s.scheduled_at)} · with {s.counsellor_name || "counsellor"}</div>
                        <div className="text-sm text-[#3D405B]">{s.summary_client}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
